// Debug utility functions
type DebugLevel = "debug" | "log" | "info" | "warn" | "error";

const getDebugCache = (): Map<string, number> => {
  const win = window as Window & {
    __debugLogCache?: Map<string, number>;
  };
  if (!win.__debugLogCache) {
    win.__debugLogCache = new Map<string, number>();
  }
  return win.__debugLogCache;
};

const emitDebug = (
  level: DebugLevel,
  message: string,
  data?: Record<string, unknown>
): void => {
  const logger = console[level] || console.log;
  if (data !== undefined) {
    logger(message, data);
  } else {
    logger(message);
  }
};

export const debugOnce = (
  key: string,
  message: string,
  data?: Record<string, unknown>,
  level: DebugLevel = "debug"
): void => {
  const cache = getDebugCache();
  if (cache.has(key)) return;
  cache.set(key, Date.now());
  emitDebug(level, message, data);
};

export const debugThrottled = (
  key: string,
  intervalMs: number,
  message: string,
  data?: Record<string, unknown>,
  level: DebugLevel = "debug"
): void => {
  const cache = getDebugCache();
  const now = Date.now();
  const last = cache.get(key) || 0;
  if (now - last < intervalMs) return;
  cache.set(key, now);
  emitDebug(level, message, data);
};

export const debugGroupCollapsedOnce = (
  key: string,
  title: string,
  entries: Array<{ label: string; value: unknown }>
): void => {
  const cache = getDebugCache();
  if (cache.has(key)) return;
  cache.set(key, Date.now());
  console.groupCollapsed(title);
  entries.forEach((entry) => {
    console.log(entry.label, entry.value);
  });
  console.groupEnd();
};

// Function to log localStorage content
export const logLocalStorage = (): void => {
  try {
    debugGroupCollapsedOnce("localStorage:summary", "LocalStorage Contents:", [
      {
        label: "finance_expenses:",
        value: localStorage.getItem("finance_expenses"),
      },
      {
        label: "finance_income:",
        value: localStorage.getItem("finance_income"),
      },
      { label: "finance_debts:", value: localStorage.getItem("finance_debts") },
      {
        label: "finance_migration_complete:",
        value: localStorage.getItem("finance_migration_complete"),
      },
      {
        label: "finance_use_api:",
        value: localStorage.getItem("finance_use_api"),
      },
    ]);
  } catch (error) {
    console.error("Error logging localStorage:", error);
  }
};

// Function to force data migration
export const forceMigration = async (): Promise<void> => {
  try {
    // Get data from localStorage
    const expenses = localStorage.getItem("finance_expenses");
    const income = localStorage.getItem("finance_income");
    const debts = localStorage.getItem("finance_debts");

    // Build request body
    const requestBody = {
      localStorage: {
        expenses,
        income,
        debts,
      },
    };

    // Send migration request to the API
    const response = await fetch("/api/migrate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    const result = await response.json();
    console.log("Migration result:", result);

    // Update migration flags
    localStorage.setItem("finance_migration_complete", "true");
    localStorage.setItem("finance_use_api", "true");
  } catch (error) {
    console.error("Error during forced migration:", error);
  }
};
